import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

import { API } from '../lib/apiBase';
const TOKEN_KEY = 'token';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setOrganization(null);
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);

      if (response.data.organization_id) {
        const orgResponse = await axios.get(`${API}/organizations/mine`);
        setOrganization(orgResponse.data);
      }
    } catch {
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;

    localStorage.setItem(TOKEN_KEY, newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);

    if (userData.organization_id) {
      const orgResponse = await axios.get(`${API}/organizations/mine`);
      setOrganization(orgResponse.data);
    }

    return userData;
  }, []);

  const register = useCallback(async (userData, orgName) => {
    const response = await axios.post(`${API}/auth/register`, userData, {
      params: orgName ? { org_name: orgName } : {}
    });
    const { token: newToken, user: newUser } = response.data;

    localStorage.setItem(TOKEN_KEY, newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(newUser);

    if (newUser.organization_id) {
      const orgResponse = await axios.get(`${API}/organizations/mine`);
      setOrganization(orgResponse.data);
    }

    return newUser;
  }, []);

  const refreshOrganization = useCallback(async () => {
    if (user?.organization_id) {
      const orgResponse = await axios.get(`${API}/organizations/mine`);
      setOrganization(orgResponse.data);
    }
  }, [user?.organization_id]);

  const contextValue = useMemo(() => ({
    user,
    token,
    organization,
    loading,
    login,
    register,
    logout,
    refreshOrganization,
    isAuthenticated: !!token && !!user,
    isAdmin: user?.role === 'admin',
    isOperator: user?.role === 'operator' || user?.role === 'admin',
  }), [user, token, organization, loading, login, register, logout, refreshOrganization]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
